import * as React from 'react';

import { SiteUrls } from '@tableau/shared-urls';
import { Messages } from '../../../codegen/Localize';
import { SiteSpaceContentTypeChangeEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { ExplorableItemDefinitions } from '../../../ts/models/ContentDefinitions';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '../../state/SiteSpaceContentTypeState';
import { TopLevelProjectId } from '../../state/SpaceInfo';
import { ConnectedExplorer } from '../Explorer/ConnectedExplorer';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { addQuery, SpacesSiteUrls } from '../SpacesSiteUrls';
import { createContentButton } from '../SubspaceHelpers';
import { SiteLevelFilterByTypeDropdown } from './SiteLevelFilterByTypeDropdown';

export interface SiteSpaceProps extends ReactDirectiveProps {
  readonly telemetry: Telemetry;
  readonly siteSpaceContentType: SiteSpaceContentType;
  readonly urls: SpacesSiteUrls;
}

export class SiteSpace extends React.PureComponent<SiteSpaceProps> {

  public render() {
    const displayModeContentType = convertSiteContentTypeToContentType(this.props.siteSpaceContentType);
    const supportedDisplayModes = ExplorableItemDefinitions[displayModeContentType].supportedDisplayModes;

    return (
      <div className={ SpaceContainerStyles.SpaceContainer }>
        <div className={ SpaceContainerStyles.MastheadArea }>
          <div className={ExplorerStyles.SpaceHeaderContent}>
            <div className={ExplorerStyles.SpaceHeaderText}>{Messages.explore()}</div>
            {this.contentFilterControl()}
          </div>
        </div>
        <div className={ SpaceContainerStyles.SubplaceArea }>
          <ConnectedExplorer
            angularContext={this.props.angularContext}
            supportedDisplayModes={supportedDisplayModes}
            telemetry={this.props.telemetry}
            toolbarProps= {{
              supportFilterPanel: true,
              toolbarVisibility: 'enabled',
              toolbarContentSpecificControls: { beginning: createContentButton(TopLevelProjectId, null) }
            }}
          />
        </div>
      </div>
    );
  }

  private changeSiteLevelContentType = (contentType: SiteSpaceContentType) => {
    this.sendSiteSpaceTelemetryEvent(contentType);

    const url = addQuery(getSiteUrlForContentType(contentType, this.props.urls));
    window.location.href = url;
  }

  private sendSiteSpaceTelemetryEvent(newContentType: SiteSpaceContentType) {
    const oldContentType = this.props.siteSpaceContentType;
    this.props.telemetry.sendEvent<SiteSpaceContentTypeChangeEvent>({
      type: TelemetryEvents.SiteSpaceContentTypeChange,
      newContentType,
      oldContentType,
    });
  }

  private contentFilterControl(): JSX.Element {
    return (
        <SiteLevelFilterByTypeDropdown currentContentType={this.props.siteSpaceContentType}
                                        changeCurrentContentType={this.changeSiteLevelContentType} />
    );
  }
}

// Return the top level url for the given content type.
function getSiteUrlForContentType(contentType: SiteSpaceContentType, urls: SiteUrls) {
  if (contentType === 'dataconnection') {
    throw new Error('invalid site-level content type: dataconnection');
  }

  const ContentTypeToUrl: Record<SiteSpaceContentType, string> = {
    dataconnection: '',
    database: '',  // handled separately
    table: '',  // handled separately
    datasource: urls.datasourcesUrl(),
    dataRole: urls.dataRolesUrl(),
    explore: urls.exploreUrl(),
    flow: urls.flowsUrl(),
    project: urls.projectsUrl(),
    view: urls.viewsUrl(),
    workbook: urls.workbooksUrl(),
    metric: urls.metricsUrl(),
    publishedConnection: urls.publishedConnectionsUrl()
  };

  const url = ContentTypeToUrl[contentType];
  if (url) {
    return url;
  }

  throw new Error('invalid site-level content type: ' + contentType);
}
